import { type Action } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';

import {
    Admin,
    type ApiResult,
    type DisplayUserExtended,
    type EmojiId,
    type InitializeUser,
    type OptIn,
    type OptInType,
    type OptinUser,
    type PaginatedResult,
    type ResourceId,
    type UpdateUserParameters,
    type UserAccount,
    UserDataClient,
    Users,
} from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { type UserData, UserDataKey } from '@app/client/common/structs/user/user-data';
import { callConsumerDraftUser, callInitializeUser } from '@app/client/resources/users';
import config from '@app/config';
import { prefetch2FA } from '@app/helpers';
import { initializeAdminInfo } from '@app/store/actions/admin-info';
import { setUserArtifacts } from '@app/store/actions/artifacts';
import { getDashboard } from '@app/store/actions/dashboard';
import { setSearchOpen } from '@app/store/actions/display';
import { setEmojiEditions } from '@app/store/actions/editions';
import { setLiquidIdCollectionId } from '@app/store/actions/nft';
import { setReduxCartItems } from '@app/store/actions/picker';
import { setIncomingTransfers, setOutgoingTransfers } from '@app/store/actions/transfers';
import { LOCAL_STORAGE_CURRENT_EMOJI_KEY } from '@app/store/reducers/user-emojis';
import { setFeatures } from '@app/store/slices/features';
import { AttributionEnum, getAttributionProperties } from '@app/store/types/attribution_properties';
import { type ErrorState } from '@app/store/types/error';
import { type FeatureFlags } from '@app/store/types/features';
import { type UserProperties } from '@app/store/types/user_properties';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import packageInfo from '../../../package.json';
import { type RootState } from '../reducers';
import { addAlert } from '../slices';
import {
    ACTION_SET_USER,
    ACTION_SET_USER_OPT_IN,
    ACTION_SET_USER_OPT_INS,
    ACTION_SET_USER_PRIMARY_YAT,
    type UserActionTypes,
} from '../types/user';

import { setError } from './error';
import { notifyEmojisRemoved, setActiveEmojiId, setLoadingEmojis, setUserExtendedEmojis } from './user-emojis';

///
/// Setter functions

export const setUser = (user: UserAccount): UserActionTypes => ({
    type: ACTION_SET_USER,
    user,
});

export const setUserOptIns = (optIns: OptIn[]): UserActionTypes => ({
    type: ACTION_SET_USER_OPT_INS,
    optIns,
});

export const setUserOptIn = (optInType: OptInType, optInValue: boolean): UserActionTypes => ({
    type: ACTION_SET_USER_OPT_IN,
    optInType,
    optInValue,
});

export const setPrimaryYat = (primaryYat: EmojiId): UserActionTypes => ({
    type: ACTION_SET_USER_PRIMARY_YAT,
    primaryYat,
});

///
/// API Factory functions
export function initializeUser() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, state: () => RootState) {
        dispatch(setLoadingEmojis(true));

        const userResponse = await callInitializeUser({});

        try {
            if (userResponse.ok && userResponse.value) {
                setUserInformation(userResponse.value, dispatch, state).then(() => {
                    const userId = localStorage.getItem('draftUserId');
                    if (userId) {
                        handleDraftUser(userId, dispatch);
                    }
                    return;
                });
            }
        } catch (error) {
            setError({ ...(error as ErrorState), source: 'User Initialisation' });
        } finally {
            dispatch(setLoadingEmojis(false));
        }
    };
}

export function setUserFeatureFlags(featureFlags: FeatureFlags) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<UserData | null>> {
        return await apiFactory()
            .call(UserDataClient.putUserData, {
                key: UserDataKey.FrontendFeatureFlags,
                value: { value: featureFlags },
            })
            .then((r) => {
                dispatch(setFeatures(featureFlags));
                return r;
            })
            .catch((e) => {
                setError({
                    ...e,
                    log: true,
                    source: 'Error getting user feature flags.',
                })();
                return e;
            });
    };
}

export function setUserAutoGenerateProfile(isAuto: boolean) {
    return async function (): Promise<ApiResult<UserData | null>> {
        return await apiFactory()
            .call(UserDataClient.putUserData, {
                key: UserDataKey.AutoGenerateProfile,
                value: { value: isAuto },
            })
            .then((r) => {
                return r;
            })
            .catch((e) => {
                setError({
                    ...e,
                    log: true,
                    source: 'Error setting user auto generated profile.',
                })();
                return e;
            });
    };
}

export function updateCurrentUser(userData: UpdateUserParameters) {
    return function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<UserAccount>> {
        return apiFactory()
            .call(Users.updateCurrentUser, { prefetch: prefetch2FA(dispatch), value: userData })
            .then((r) => {
                if (!(r && r.ok)) {
                    // hides validation error notification when the password is updated (since it already is displayed in SetPasswordForm)
                    if (r.error && (r.error as string) !== 'Validation error') {
                        dispatch(setError({ ...r, notify: true, source: 'Error updating user.' }));
                    }
                }
                return r;
            })
            .catch((e) => {
                dispatch(
                    setError({
                        ...e,
                        log: true,
                        source: 'Error updating user.',
                    }),
                );
                return e;
            });
    };
}

export const fetchUsers = (query = '', page = 0, limit = 20) => {
    return function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ApiResult<PaginatedResult<DisplayUserExtended>> | void> {
        return apiFactory()
            .call(Admin.Users.getCollection, {
                query: {
                    q: query,
                    page,
                    limit,
                },
            })
            .then((r) => {
                if (r) {
                    return r;
                }
            })
            .catch((e) => {
                if (e) {
                    dispatch(addAlert({ message: e.message }));
                }
            });
    };
};

interface UpdateUserOptIn {
    optInType: OptInType;
    optInValue: boolean;
    optInText: string;
}

export const updateUserOptIn = ({ optInType, optInValue }: UpdateUserOptIn) => {
    return function async(dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<OptinUser> | void> {
        return apiFactory()
            .call(Users.optInUser, {
                value: { opted_in: optInValue },
                optInType,
            })
            .then((r) => {
                if (r && r.ok) {
                    dispatch(setUserOptIn(optInType, r.value.opted_in));
                }
                return r;
            })
            .catch((e) => {
                setError({ ...e, log: true, source: 'Error updating User opt in' });
            });
    };
};

export const setUserInformation = async (
    userResponseValue: InitializeUser,
    dispatch: ThunkDispatch<RootState, void, Action>,
    state: () => RootState,
) => {
    const referrer = document.referrer;
    const {
        artifact_breakdown,
        current_user,
        editions,
        extended_list,
        frontend_feature_flags,
        incoming_transfers,
        liquid_id,
        outgoing_transfers,
        sidebar,
        twitter_data,
        websocket_auth_token,
        opt_ins,
    } = userResponseValue;

    // Setting feature flag before user account initialization
    if (frontend_feature_flags && frontend_feature_flags.key == UserDataKey.FrontendFeatureFlags) {
        const featureFlags = frontend_feature_flags.value as FeatureFlags;
        dispatch(setFeatures(featureFlags));
    }

    const user = current_user.user;
    const userProperties: UserProperties = {
        email: user.email,
    };

    const { showAdminInfo, enableWebsockets } = state().featureFlags.features;
    const userAuthToken = useAuthTokens.getState().token?.access_token;

    if (enableWebsockets) {
        await apiFactory().initializeWebsocket(false, websocket_auth_token, userAuthToken);
    }

    const attributionProperties = getAttributionProperties();
    if (attributionProperties) {
        Object.keys(AttributionEnum).forEach((key) => {
            if (attributionProperties[key]) {
                userProperties[key] = attributionProperties[key];
            }
        });
    }

    if (referrer !== '') {
        const domain_anchor = document.createElement('a');
        domain_anchor.href = referrer;
        userProperties.referrer = referrer;
        userProperties.referring_domain = domain_anchor.hostname;
    }
    window.analytics.identify(user.id, userProperties, {
        context: {
            app: { version: packageInfo.version },
        },
    });
    if (config.fbPixelId) {
        window.fbq('init', config.fbPixelId, { external_id: user.id, em: user.email });
        window.fbq('track', 'PageView');
    }

    dispatch(setUser({ ...current_user, twitter_data }));
    dispatch(setLiquidIdCollectionId(liquid_id));

    dispatch(setEmojiEditions(editions));
    dispatch(setOutgoingTransfers(outgoing_transfers.data));
    dispatch(setIncomingTransfers(incoming_transfers));
    dispatch(setUserExtendedEmojis(extended_list));

    if (opt_ins) {
        dispatch(setUserOptIns(opt_ins));
    }

    const primaryYat = current_user.user.eid;
    const primaryYatConfirmed = current_user.user.eid_status;

    if (primaryYat && primaryYatConfirmed) {
        dispatch(setPrimaryYat(primaryYat));
    }

    await dispatch(getDashboard(sidebar));
    dispatch(setUserArtifacts(artifact_breakdown));

    const currentEmoji = //set current emoji - if previously stored, else first available
        state().userEmojis.currentEmoji ||
        localStorage.getItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY) ||
        extended_list?.[0]?.display_format;

    if (currentEmoji) {
        await dispatch(setActiveEmojiId(currentEmoji, extended_list));
    }

    dispatch(setSearchOpen(extended_list.length === 0));

    if (state().user.userClass?.isAdmin || showAdminInfo) {
        await dispatch(initializeAdminInfo());
    }
    await dispatch(notifyEmojisRemoved());
};

export const handleDraftUser = async (userId: ResourceId, dispatch: ThunkDispatch<RootState, void, Action>) => {
    const draftUserResponse = await callConsumerDraftUser({ value: { userId } });
    try {
        if (draftUserResponse.ok && draftUserResponse.value) {
            await dispatch(setReduxCartItems(draftUserResponse.value));
            localStorage.removeItem('draftUserId'); //todo add to UserData?
        }
    } catch (e) {
        dispatch(setError({ ...(e as ErrorState), source: 'Draft User Initialisation' }));
    }
};
